/* ============================================================================
 * cabinet-recolor.js  —  RTA Wholesalers cabinet color visualizer engine (v2)
 *
 * MASK-BASED. Each product photo has a pre-generated silhouette mask, built by
 * scripts/usamca/generate_recolor_masks.py (rembg + clean-up) and served from
 * opcabinets.com/recolor-masks/ with CORS headers. The engine recolors ONLY
 * inside the mask:
 *   - FRONT (face frame + drawer fronts + doors)  -> customer's SW color
 *   - BOX  (warm wood: sides / interior / toe-kick) -> locked Accessible Beige
 *
 * If an image has NO mask in the manifest, it is NOT recolored at all — the
 * original photo stays. Nothing ugly can ship: no mask, no paint.
 * ==========================================================================*/
window.CabinetRecolor = (function () {
  'use strict';

  var BOX_BEIGE = [0xD1, 0xC7, 0xB8];   // Accessible Beige SW 7036 (locked box color)
  var LP = 0.84;                         // luminance treated as a flat painted face
  var HI = 0.18;                         // how far highlights lift toward white
  var FLOOR = 0.40;                      // darkest shadow keeps this fraction of the color
  var STORE_KEY = 'rta_front_color';
  var MASK_BASE = window.RTA_MASK_BASE || 'https://opcabinets.com/recolor-masks/';

  function hexToRgb(hex) {
    return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
  }

  /* --- mask manifest (fetched once per page) ------------------------------ */
  var manifestPromise = null;
  function manifest() {
    if (!manifestPromise) {
      // no-cache: revalidate every page load (a cheap 304 when unchanged), so a
      // newly added mask works at once instead of after the 24h browser cache
      manifestPromise = fetch(MASK_BASE + 'manifest.json', { cache: 'no-cache' })
        .then(function (r) { return r.ok ? r.json() : {}; })
        .catch(function () { return {}; });
    }
    return manifestPromise;
  }

  // CDN filename -> mask key. Mirrors mask_key() in the Python generator.
  function maskKey(url) {
    var name = String(url || '').split('?')[0];
    name = name.substring(name.lastIndexOf('/') + 1);
    name = name.replace(/\.(jpe?g|png|webp|gif)$/i, '');
    name = name.replace(/_(\d+x\d*|\d*x\d+|pico|icon|thumb|small|compact|medium|large|grande|original|master)$/i, '');
    // Shopify door-reference photos use descriptive filenames; their approved
    // masks are published under the canonical style keys.
    var lower = name.toLowerCase();
    var doorKey = lower.indexOf('bevel-slim-shaker') !== -1 ? 'BSS-01' :
      lower.indexOf('flat-slim-shaker') !== -1 ? 'FSS-01' :
      lower.indexOf('slim-shaker-raised-panel') !== -1 ? 'SSRP-01' :
      lower.indexOf('double-line-shaker') !== -1 ? 'DLS-01' :
      lower.indexOf('flat-panel-shaker') !== -1 ? 'FPS-01' :
      lower.indexOf('raised-groove-shaker') !== -1 ? 'RGS-01' :
      lower.indexOf('groove-shaker') !== -1 ? 'GS-01' :
      lower.indexOf('raised-panel') !== -1 ? 'B27-FRONT' :
      lower.indexOf('sladhxpzmqghawss') !== -1 ? 'BSS-01' :
      lower.indexOf('zqhxthxljpppqpwp') !== -1 ? 'FSS-01' :
      lower.indexOf('syafwodicyedpuna') !== -1 ? 'SSRP-01' :
      lower.indexOf('icnleowcitbdkxhf') !== -1 ? 'DLS-01' :
      lower.indexOf('posmzbaenmatrpfj') !== -1 ? 'FPS-01' :
      lower.indexOf('dzwvxirvuvlgrxfo') !== -1 ? 'RGS-01' :
      lower.indexOf('yxvdscaupucouxpy') !== -1 ? 'GS-01' : null;
    return doorKey || name;
  }

  /* --- segmentation from a mask ------------------------------------------- */
  function segmentWithMask(img, maskImg, size) {
    var maxDim = size || 760;
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    var scale = Math.min(1, maxDim / Math.max(iw, ih));
    var W = Math.max(1, Math.round(iw * scale)), H = Math.max(1, Math.round(ih * scale));
    var N = W * H;

    var pc = document.createElement('canvas'); pc.width = W; pc.height = H;
    var pcx = pc.getContext('2d', { willReadFrequently: true });
    pcx.drawImage(img, 0, 0, W, H);
    var d = pcx.getImageData(0, 0, W, H).data;

    var mc = document.createElement('canvas'); mc.width = W; mc.height = H;
    var mcx = mc.getContext('2d', { willReadFrequently: true });
    mcx.drawImage(maskImg, 0, 0, W, H);
    var md = mcx.getImageData(0, 0, W, H).data;

    var alpha = new Uint8Array(N), wood = new Uint8Array(N), lum = new Float32Array(N);
    for (var i = 0; i < N; i++) {
      var k = i * 4;
      // masks are grayscale: red channel == alpha level
      alpha[i] = md[k];
      var r = d[k], g = d[k + 1], b = d[k + 2];
      lum[i] = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      if (alpha[i] > 100 && (r - b) > 34 && r >= g - 4 && g >= b - 4) wood[i] = 1;
    }
    // smooth the wood mask so front/box regions are coherent, not patchy
    var wnext = new Uint8Array(wood);
    for (var wi = 0; wi < N; wi++) {
      var wx = wi % W, wy = (wi - wx) / W;
      if (wx === 0 || wy === 0 || wx === W - 1 || wy === H - 1 || alpha[wi] <= 100) continue;
      var ws = wood[wi - 1] + wood[wi + 1] + wood[wi - W] + wood[wi + W] +
               wood[wi - W - 1] + wood[wi - W + 1] + wood[wi + W - 1] + wood[wi + W + 1];
      if (!wood[wi] && ws >= 6) wnext[wi] = 1;
      else if (wood[wi] && ws <= 2) wnext[wi] = 0;
    }
    wood = wnext;
    return { W: W, H: H, N: N, alpha: alpha, wood: wood, lum: lum, src: d,
             box: findBox(d, alpha, W, H), out: pcx.createImageData(W, H) };
  }

  /* --- Premium Box: the cabinet box renders like Custom Oak --------------- */
  // The photographed box is a flat orange placeholder. It is found in the photo
  // itself (same classifier as oak-box-visualizer.js) and painted with the
  // Premium Box grain texture (SW 8917 Shell White + SW 7573 Eaglet Beige),
  // lit by the photo's own shading. Doors/face frame (the mask) never change.
  var TEX = null, texPromise = null;
  function loadBoxTexture(url) {
    if (texPromise || !url) return texPromise || Promise.resolve(null);
    texPromise = new Promise(function (resolve) {
      var t = new Image();
      t.crossOrigin = 'anonymous';
      t.onload = function () {
        try {
          var c = document.createElement('canvas');
          c.width = t.naturalWidth; c.height = t.naturalHeight;
          var x = c.getContext('2d', { willReadFrequently: true });
          x.drawImage(t, 0, 0);
          TEX = { w: c.width, h: c.height, d: x.getImageData(0, 0, c.width, c.height).data };
        } catch (e) { TEX = null; }
        resolve(TEX);
      };
      t.onerror = function () { resolve(null); };
      t.src = url;
    });
    return texPromise;
  }

  var TL = [0, 50, 75, 100, 125, 150, 175, 200, 256], TS = [0.60, 0.56, 0.55, 0.55, 0.555, 0.48, 0.42, 0.40, 0.38];
  function satThreshold(L) {
    for (var j = 1; j < TL.length; j++) if (L < TL[j]) return TS[j - 1] + (TS[j] - TS[j - 1]) * (L - TL[j - 1]) / (TL[j] - TL[j - 1]);
    return 0.38;
  }
  // flip 4-connected regions of value `val` smaller than `min` px
  function dropSmall(m, W, H, min, val) {
    var N = W * H, seen = new Uint8Array(N), stack = new Int32Array(N), region = new Int32Array(N);
    for (var s0 = 0; s0 < N; s0++) {
      if (seen[s0] || m[s0] !== val) continue;
      var sp = 0, n = 0; stack[sp++] = s0; seen[s0] = 1;
      while (sp) {
        var p = stack[--sp]; region[n++] = p; var px = p % W, nb;
        nb = p - 1; if (px > 0 && !seen[nb] && m[nb] === val) { seen[nb] = 1; stack[sp++] = nb; }
        nb = p + 1; if (px < W - 1 && !seen[nb] && m[nb] === val) { seen[nb] = 1; stack[sp++] = nb; }
        nb = p - W; if (p >= W && !seen[nb] && m[nb] === val) { seen[nb] = 1; stack[sp++] = nb; }
        nb = p + W; if (p < N - W && !seen[nb] && m[nb] === val) { seen[nb] = 1; stack[sp++] = nb; }
      }
      if (n < min) for (var q = 0; q < n; q++) m[region[q]] = 1 - val;
    }
  }
  // mean over a (2R+1)x(2R+1) window, edge-clamped (integral image)
  function boxMean(src, W, H, R) {
    var S = new Float64Array((W + 1) * (H + 1)), out = new Float32Array(W * H), x, y;
    for (y = 0; y < H; y++) {
      var row = 0;
      for (x = 0; x < W; x++) { row += src[y * W + x]; S[(y + 1) * (W + 1) + x + 1] = S[y * (W + 1) + x + 1] + row; }
    }
    for (y = 0; y < H; y++) {
      var y0 = Math.max(0, y - R), y1 = Math.min(H, y + R + 1);
      for (x = 0; x < W; x++) {
        var x0 = Math.max(0, x - R), x1 = Math.min(W, x + R + 1);
        out[y * W + x] = (S[y1 * (W + 1) + x1] - S[y0 * (W + 1) + x1] - S[y1 * (W + 1) + x0] + S[y0 * (W + 1) + x0]) / ((y1 - y0) * (x1 - x0));
      }
    }
    return out;
  }
  // Orange placeholder-box pixels outside the painted-front mask, or null.
  function findBox(d, alpha, W, H) {
    var N = W * H, box = new Uint8Array(N), count = 0;
    for (var i = 0, k = 0; i < N; i++, k += 4) {
      if (alpha[i] > 100 || d[k + 3] < 20) continue;      // fronts stay fronts
      var r = d[k], g = d[k + 1], b = d[k + 2];
      if (!(r > g && g >= b)) continue;
      var L = 0.299 * r + 0.587 * g + 0.114 * b;
      if (L < 120 && g >= 0.70 * r) continue;
      if ((r - b) / r > satThreshold(L)) { box[i] = 1; count++; }
    }
    if (count < N * 0.01) return null;                     // no box in this photo
    dropSmall(box, W, H, 40, 1);                           // specks
    // Shadowed box corners and the toe kick drift toward tan but stay smooth:
    // flood from the box through smooth orange-hue pixels (as the oak tool does).
    var lumA = new Float32Array(N), hueOk = new Uint8Array(N);
    for (i = 0, k = 0; i < N; i++, k += 4) {
      lumA[i] = 0.299 * d[k] + 0.587 * d[k + 1] + 0.114 * d[k + 2];
      if (alpha[i] <= 100 && d[k + 3] >= 20 && d[k] > d[k + 1] && d[k + 1] >= d[k + 2] - 2 &&
          (d[k] - d[k + 2]) / d[k] > 0.12) hueOk[i] = 1;
    }
    var m3 = boxMean(lumA, W, H, 1), det = new Float32Array(N);
    for (i = 0; i < N; i++) det[i] = Math.abs(lumA[i] - m3[i]);
    var grn = boxMean(det, W, H, 3), stack = new Int32Array(N), sp = 0;
    for (i = 0; i < N; i++) if (box[i]) stack[sp++] = i;
    while (sp) {
      var p = stack[--sp], px = p % W, nbs = [px > 0 ? p - 1 : -1, px < W - 1 ? p + 1 : -1, p - W, p + W];
      for (var z = 0; z < 4; z++) {
        var q = nbs[z];
        if (q >= 0 && q < N && !box[q] && hueOk[q] && grn[q] < 2.5) { box[q] = 1; stack[sp++] = q; }
      }
    }
    dropSmall(box, W, H, 40, 0);                           // pinholes
    // anti-aliased rim: grow 2px into any remaining orange-hue pixel
    for (var pass = 0; pass < 2; pass++) {
      var grow = [];
      for (i = 0; i < N; i++) {
        if (box[i] || !hueOk[i]) continue;
        var x0 = i % W;
        if ((x0 > 0 && box[i - 1]) || (x0 < W - 1 && box[i + 1]) || (i >= W && box[i - W]) || (i < N - W && box[i + W])) grow.push(i);
      }
      for (var gi = 0; gi < grow.length; gi++) box[grow[gi]] = 1;
    }
    var hist = new Uint32Array(256), cnt = 0, h;
    for (i = 0; i < N; i++) if (box[i]) {
      k = i * 4; hist[(0.299 * d[k] + 0.587 * d[k + 1] + 0.114 * d[k + 2]) | 0]++; cnt++;
    }
    var target = cnt * 0.9, acc = 0, Lref = 150;
    for (h = 0; h < 256; h++) { acc += hist[h]; if (acc >= target) { Lref = h || 1; break; } }
    return { m: box, Lref: Lref };
  }
  // paint one box pixel from the grain texture, shaded like oak-box-visualizer
  function boxPx(o, k, d, x, y, W, Lref) {
    var sc = Math.max(0.25, W / 1400);
    var tk = ((((y / sc) | 0) % TEX.h) * TEX.w + (((x / sc) | 0) % TEX.w)) * 4, td = TEX.d;
    var L = 0.299 * d[k] + 0.587 * d[k + 1] + 0.114 * d[k + 2];
    var shade = L / Lref; if (shade > 1.1) shade = 1.1;
    var f = shade < 1 ? 0.40 + 0.60 * shade : shade, fc = f < 1 ? f : 1;
    o[k]     = Math.min(255, td[tk]     * f);
    o[k + 1] = Math.min(255, td[tk + 1] * f * (0.90 + 0.10 * fc));
    o[k + 2] = Math.min(255, td[tk + 2] * f * (0.74 + 0.26 * fc));
  }

  // luminance-preserving repaint of one pixel onto a target color
  function paintPx(L, col) {
    if (L <= LP) { var f = FLOOR + (1 - FLOOR) * (L / LP); return [col[0] * f, col[1] * f, col[2] * f]; }
    var t = Math.min((L - LP) / (1 - LP), 1) * HI;
    return [col[0] + (255 - col[0]) * t, col[1] + (255 - col[1]) * t, col[2] + (255 - col[2]) * t];
  }

  function repaint(canvas, seg, frontHex, opts) {
    opts = opts || {};
    var box = opts.box || BOX_BEIGE;
    var FRONT = frontHex ? hexToRgb(frontHex) : null;
    var W = seg.W, H = seg.H, N = seg.N, o = seg.out.data, d = seg.src;
    // Standard Box: the natural birch box as photographed, front still painted.
    var standard = !!opts.standardBox;
    var premium = !standard && !!(TEX && opts.premiumBox);
    var keep = premium || standard;                    // photo kept outside the mask
    var pbox = premium ? seg.box : null;
    canvas.width = W; canvas.height = H;
    for (var i = 0; i < N; i++) {
      var k = i * 4, a = seg.alpha[i];
      if (pbox && pbox.m[i]) {                         // premium box, grain + shading
        boxPx(o, k, d, i % W, (i - i % W) / W, W, pbox.Lref);
        o[k + 3] = d[k + 3];
        continue;
      }
      if (a < 12) {
        if (keep) {                                 // keep the photo as shot
          o[k] = d[k]; o[k + 1] = d[k + 1]; o[k + 2] = d[k + 2]; o[k + 3] = d[k + 3];
        } else o[k + 3] = 0;                           // legacy: outside the mask transparent
        continue;
      }
      if (standard && seg.wood[i]) {                   // birch box as shot
        o[k] = d[k]; o[k + 1] = d[k + 1]; o[k + 2] = d[k + 2]; o[k + 3] = d[k + 3];
        continue;
      }
      if (premium && seg.wood[i]) {                    // warm rim inside the mask
        boxPx(o, k, d, i % W, (i - i % W) / W, W, pbox ? pbox.Lref : 200);
        o[k + 3] = d[k + 3];
        continue;
      }
      if (!FRONT) {                                    // no color picked yet: front as shot
        o[k] = d[k]; o[k + 1] = d[k + 1]; o[k + 2] = d[k + 2]; o[k + 3] = keep ? d[k + 3] : a;
        continue;
      }
      var col = seg.wood[i] ? box : FRONT;
      var pp = paintPx(seg.lum[i], col);
      if (keep && a < 255) {                        // feather the front edge into the photo
        var t = a / 255;
        o[k] = (pp[0] * t + d[k] * (1 - t)) | 0;
        o[k + 1] = (pp[1] * t + d[k + 1] * (1 - t)) | 0;
        o[k + 2] = (pp[2] * t + d[k + 2] * (1 - t)) | 0;
        o[k + 3] = d[k + 3];
        continue;
      }
      o[k] = pp[0] | 0; o[k + 1] = pp[1] | 0; o[k + 2] = pp[2] | 0;
      o[k + 3] = a;                                    // feathered edges from the mask
    }
    canvas.getContext('2d').putImageData(seg.out, 0, 0);
  }

  /* --- Instance: mask lookup + segment on load, then setColor() any time --- */
  function Instance(img, canvas, opts) {
    this.canvas = canvas; this.opts = opts || {};
    this.ready = false; this.disabled = false; this._pending = null;
    var self = this;

    function withPhoto(fn) {
      if (img.complete && img.naturalWidth) fn();
      else img.addEventListener('load', fn, { once: true });
    }
    manifest().then(function (map) {
      var entry = map[maskKey(img.src)] || (window.RTA_MASK_EXTRA || {})[maskKey(img.src)];
      if (!entry) { self.disabled = true; return; }
      var maskImg = new Image();
      maskImg.crossOrigin = 'anonymous';
      maskImg.onload = function () {
        withPhoto(function () {
          self.seg = segmentWithMask(img, maskImg, self.opts.size || 760);
          self.ready = true;
          if (self._pending) self.setColor(self._pending);
          if (self.opts.onReady) self.opts.onReady(self);
        });
      };
      maskImg.onerror = function () { self.disabled = true; };
      maskImg.src = entry.url || (MASK_BASE + entry.file);  // theme-hosted extra masks carry a full url
    });
  }
  Instance.prototype.setColor = function (hex) {
    if (this.disabled) return;
    if (!this.ready) { this._pending = hex; return; }
    repaint(this.canvas, this.seg, hex, this.opts); this.hex = hex;
  };
  Instance.prototype.snapshotDataURL = function (type) { return this.canvas.toDataURL(type || 'image/png'); };
  Instance.prototype.snapshotBlob = function (cb, type) { this.canvas.toBlob(cb, type || 'image/png'); };

  /* --- persistence --------------------------------------------------------- */
  function save(sel) { try { localStorage.setItem(STORE_KEY, JSON.stringify(sel)); } catch (e) {} }
  function load() { try { return JSON.parse(localStorage.getItem(STORE_KEY)); } catch (e) { return null; } }
  function clear() { try { localStorage.removeItem(STORE_KEY); } catch (e) {} }

  // Write the chosen color as a Shopify cart attribute (carries to checkout/order).
  function saveToCart(sel) {
    return fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Front color': sel.code ? sel.name + ' ' + sel.code : sel.name } })
    });
  }

  /* --- skip diagrams / spec art -------------------------------------------- */
  function shouldSkip(imgEl) {
    if (imgEl.dataset && imgEl.dataset.noRecolor !== undefined) return true;
    var src = (imgEl.getAttribute('src') || '').toLowerCase();
    var alt = (imgEl.getAttribute('alt') || '').toLowerCase();
    return /-spec|-diagram|diagram|dimension/.test(src + ' ' + alt);
  }

  return {
    BOX_BEIGE: BOX_BEIGE, STORE_KEY: STORE_KEY, MASK_BASE: MASK_BASE,
    maskKey: maskKey, Instance: Instance,
    save: save, load: load, clear: clear, saveToCart: saveToCart, shouldSkip: shouldSkip,
    loadBoxTexture: loadBoxTexture
  };
})();